/**
 * PostHog singleton wrapper.
 *
 * Env-guarded: when `VITE_POSTHOG_KEY` is unset the module exports no-ops, so
 * the rest of the app can call `capture/identify/reset` unconditionally without
 * a boot-time failure. The capture wrapper also swallows runtime errors to
 * honor the "never throw from analytics" contract used by
 * `usePremiumTriggerAnalytics`.
 *
 * SDK config notes:
 *   - `capture_pageview: false`  — pageviews are emitted manually on route
 *     change (SPA needs this to fire on client-side navigations).
 *   - `disable_session_recording: true` — Session Replay is off in code, so
 *     switching it on in PostHog project settings has no effect. The Privacy
 *     Policy does not disclose session recording. Before re-enabling, update
 *     the Policy and mask every view that renders private content (DMs,
 *     Planner notes): `maskAllInputs` covers form fields only, not text
 *     already rendered on screen.
 *   - `person_profiles: 'identified_only'` — avoids creating profiles for
 *     anonymous traffic; events still flow with an anonymous distinct_id.
 *
 * Person properties: `identify` carries the user's email and name so the
 * PostHog person can be found and contacted. That is the only PII sent; event
 * properties stay free of it (see ANALYTICS_EVENT_MAP.md, privacy notes).
 */

import posthog from 'posthog-js';

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';

let initialized = false;
// Last `distinct_id` passed to `identify` in this session. Used to skip
// duplicate identify calls: Supabase's `onAuthStateChange` fires SIGNED_IN
// on initial session restore, token refresh fallbacks, and OAuth callback,
// not just on a fresh sign-in. Each duplicate identify is billed as a
// `$identify` event.
let lastDistinctId: string | null = null;

export function initPostHog(): void {
  if (initialized || !KEY || typeof window === 'undefined') return;
  try {
    posthog.init(KEY, {
      api_host: HOST,
      autocapture: true,
      capture_pageview: false,
      disable_session_recording: true,
      person_profiles: 'identified_only',
    });
    initialized = true;
  } catch {
    /* never throw from analytics */
  }
}

export function isPostHogEnabled(): boolean {
  return initialized;
}

export function capture(event: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    posthog.capture(event, properties);
  } catch {
    /* never throw from analytics */
  }
}

export function identify(distinctId: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  if (distinctId === lastDistinctId) return;
  try {
    posthog.identify(distinctId, properties);
    lastDistinctId = distinctId;
  } catch {
    /* never throw from analytics */
  }
}

/**
 * Register super properties: attached to every subsequent event. PostHog
 * persists them, so callers re-register on every page load to keep them
 * current for the session.
 */
export function register(properties: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    posthog.register(properties);
  } catch {
    /* never throw from analytics */
  }
}

/** Drop a persisted super property so it stops riding on events. */
export function unregister(property: string): void {
  if (!initialized) return;
  try {
    posthog.unregister(property);
  } catch {
    /* never throw from analytics */
  }
}

/**
 * `$set` person properties on the identified user. No-op until `identify`
 * ran, so anonymous traffic never creates a person profile.
 */
export function setPersonProperties(properties: Record<string, unknown>): void {
  if (!initialized || !lastDistinctId) return;
  try {
    posthog.setPersonProperties(properties);
  } catch {
    /* never throw from analytics */
  }
}

export function reset(): void {
  if (!initialized) return;
  try {
    posthog.reset();
    lastDistinctId = null;
  } catch {
    /* never throw from analytics */
  }
}
